import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Shift.module.css";
import {
  CustomButton,
  CustomTextInput,
  CustomTimePicker,
} from "../../../../components";
import { FaCalendarAlt } from "react-icons/fa";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import dayjs from "dayjs";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useSelector } from "react-redux";

function ShiftCreate() {
  const location = useLocation();
  const navigate = useNavigate();

  // Get the id from navigation state (passed from list page)
  const shiftId = location.state?.id || "";
  const schoolId = location.state?.schoolId || "";

  // Get user data from TokenService
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || location.state?.schoolId || ""; // Get from state or TokenService
  const user_id = user?.id || "";

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { translations } = useSelector((state) => state.language);
  const [formData, setFormData] = useState({
    shiftName: "",
    fromTime: null,
    toTime: null,
    numberOfPeriods: "8",
    numberOfBreaks: "",
    breaks: [],
    periods: [],
  });

  const [errors, setErrors] = useState({});

  /* ---------------- FETCH SHIFT DATA IF ID IS PRESENT ---------------- */
  useEffect(() => {
    const fetchShiftData = async () => {
      if (!shiftId) return;

      setIsEditMode(true);
      setIsFetching(true);

      try {
        const response = await AxiosInstance.get(
          `/school/shiftView/${shiftId}`,
        );

        if (response.data.status && response.data.data) {
          const { shift, shiftBreak } = response.data.data;

          const periods = [];
          const breaks = [];

          shiftBreak.forEach((item) => {
            if (item.break_type === 1) {
              // PERIOD
              periods.push({
                id: item.id,
                periodName: item.name,
                from: item.break_start_time
                  ? dayjs(`2000-01-01T${item.break_start_time}`)
                  : null,
                to: item.break_end_time
                  ? dayjs(`2000-01-01T${item.break_end_time}`)
                  : null,
              });
            } else if (item.break_type === 0) {
              // BREAK
              breaks.push({
                id: item.id,
                breakType: item.name,
                from: item.break_start_time
                  ? dayjs(`2000-01-01T${item.break_start_time}`)
                  : null,
                to: item.break_end_time
                  ? dayjs(`2000-01-01T${item.break_end_time}`)
                  : null,
              });
            }
          });

          setFormData({
            shiftName: shift.shift_type || "",
            fromTime: shift.shift_from_time
              ? dayjs(`2000-01-01T${shift.shift_from_time}`)
              : null,
            toTime: shift.shift_to_time
              ? dayjs(`2000-01-01T${shift.shift_to_time}`)
              : null,
            numberOfPeriods: shift.no_of_period?.toString() || "",
            numberOfBreaks: shift.no_of_break?.toString() || "",
            periods,
            breaks,
          });
        } else {
          setModalState({
            open: true,
            type: "error",
            message:
              response.data?.message ||
              "Shift data not found. Creating new shift.",
            redirectPath: "",
          });

          setIsEditMode(false);
        }
      } catch (error) {
        console.error("Error fetching shift data:", error);
        setModalState({
          open: true,
          type: "error",
          message: "Failed to load shift data. Creating new shift.",
          redirectPath: "",
        });
        setIsEditMode(false);
      } finally {
        setIsFetching(false);
      }
    };

    fetchShiftData();
  }, [shiftId]);

  /* ---------------- FIXED TIME HELPER FUNCTIONS ---------------- */

  // Convert any time format to minutes since midnight
  const timeToMinutes = (timeValue) => {
    if (!timeValue) return null;

    try {
      // If it's a Day.js object
      if (dayjs.isDayjs(timeValue)) {
        return timeValue.hour() * 60 + timeValue.minute();
      }

      // If it's already in HH:MM format (from API)
      if (typeof timeValue === "string" && timeValue.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = timeValue.split(":").map(Number);
        return hours * 60 + minutes;
      }

      // If it's from time picker (string format)
      if (typeof timeValue === "string") {
        const timeStr = timeValue.trim().toUpperCase();
        const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (!match) return null;

        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3] ? match[3].toUpperCase() : "";

        // Convert to 24-hour format
        if (period === "PM" && hours !== 12) {
          hours += 12;
        } else if (period === "AM" && hours === 12) {
          hours = 0;
        }

        return hours * 60 + minutes;
      }

      return null;
    } catch (error) {
      console.error("Error parsing time:", timeValue, error);
      return null;
    }
  };

  // Format time for display in error messages (12-hour format with AM/PM)
  const formatTimeForDisplay = (timeValue) => {
    if (!timeValue) return "";

    const minutes = timeToMinutes(timeValue);
    if (minutes === null) return "";

    let hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? "PM" : "AM";

    // Convert to 12-hour format
    hours = hours % 12;
    if (hours === 0) hours = 12;

    return `${hours}:${mins.toString().padStart(2, "0")} ${period}`;
  };

  const isFromBeforeTo = (from, to) => {
    if (!from || !to) return true;

    const fromMinutes = timeToMinutes(from);
    const toMinutes = timeToMinutes(to);

    if (fromMinutes === null || toMinutes === null) return true;

    return fromMinutes < toMinutes;
  };

  const isWithinShift = (from, to) => {
    const { fromTime, toTime } = formData;
    if (!from || !to || !fromTime || !toTime) return true;

    const fromMinutes = timeToMinutes(from);
    const toMinutes = timeToMinutes(to);
    const shiftFromMinutes = timeToMinutes(fromTime);
    const shiftToMinutes = timeToMinutes(toTime);

    if (
      fromMinutes === null ||
      toMinutes === null ||
      shiftFromMinutes === null ||
      shiftToMinutes === null
    )
      return true;

    return fromMinutes >= shiftFromMinutes && toMinutes <= shiftToMinutes;
  };

  const hasOverlap = (range1Start, range1End, range2Start, range2End) => {
    if (!range1Start || !range1End || !range2Start || !range2End) return false;

    const r1Start = timeToMinutes(range1Start);
    const r1End = timeToMinutes(range1End);
    const r2Start = timeToMinutes(range2Start);
    const r2End = timeToMinutes(range2End);

    if (
      r1Start === null ||
      r1End === null ||
      r2Start === null ||
      r2End === null
    )
      return false;

    // Check if ranges overlap (end time is exclusive to allow back-to-back scheduling)
    return r1Start < r2End && r2Start < r1End;
  };

  /* ---------------- HANDLERS ---------------- */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  const handleTimeChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  const handleDelete = async () => {
    try {
      await AxiosInstance.delete("/school/deleteShift", {
        data: {
          id: shiftId,
          school_id: school_id,
        },
      });

      setDeleteModalOpen(false);

      setModalState({
        open: true,
        type: "success",
        message: "Shift deleted successfully!",
        redirectPath: "/user/timetable",
      });
    } catch (err) {
      console.error("Error deleting shift:", err);

      setDeleteModalOpen(false);

      setModalState({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          "Failed to delete shift. Please try again.",
        redirectPath: "",
      });
    }
  };

  /* ---------------- DYNAMIC BREAKS ---------------- */

  useEffect(() => {
    const count = Number(formData.numberOfBreaks) || 0;

    // Preserve existing breaks when adjusting count
    setFormData((prev) => {
      const existingBreaks = prev.breaks || [];
      const newBreaks = Array.from({ length: count }, (_, i) => {
        // If we have existing break at this index, preserve it (including ID for edit mode)
        if (i < existingBreaks.length) {
          return existingBreaks[i];
        }
        // Otherwise create new break
        return {
          id: "", // Empty string for create mode
          breakType: "",
          from: null,
          to: null,
        };
      });

      return {
        ...prev,
        breaks: newBreaks,
      };
    });
  }, [formData.numberOfBreaks]);

  /* ---------------- DYNAMIC PERIODS ---------------- */

  useEffect(() => {
    const count = Number(formData.numberOfPeriods) || 0;

    // Preserve existing periods when adjusting count
    setFormData((prev) => {
      const existingPeriods = prev.periods || [];
      const newPeriods = Array.from({ length: count }, (_, i) => {
        // If we have existing period at this index, preserve it (including ID for edit mode)
        if (i < existingPeriods.length) {
          return existingPeriods[i];
        }
        // Otherwise create new period
        return {
          id: "", // Empty string for create mode
          periodName: `Period ${i + 1}`,
          from: null,
          to: null,
        };
      });

      return {
        ...prev,
        periods: newPeriods,
      };
    });
  }, [formData.numberOfPeriods]);

  /* ---------------- FIXED VALIDATION ---------------- */

  const validate = () => {
    const newErrors = {};

    // Shift validations
    if (!formData.shiftName.trim()) {
      newErrors.shiftName = "Shift Name is required";
    }

    if (!formData.fromTime) {
      newErrors.fromTime = "From Time is required";
    }

    if (!formData.toTime) {
      newErrors.toTime = "To Time is required";
    }

    // Validate shift timing (From Time must be before To Time)
    if (formData.fromTime && formData.toTime) {
      if (!isFromBeforeTo(formData.fromTime, formData.toTime)) {
        newErrors.toTime = "Shift end time must be after start time";
      }
    }

    // No. of Periods validation
    if (!formData.numberOfPeriods) {
      newErrors.numberOfPeriods = "Number of Periods is required";
    } else if (Number(formData.numberOfPeriods) <= 0) {
      newErrors.numberOfPeriods = "Number of Periods must be greater than 0";
    } else if (Number(formData.numberOfPeriods) > 20) {
      newErrors.numberOfPeriods = "Number of Periods cannot exceed 20";
    }

    // No. of Breaks validation
    if (!formData.numberOfBreaks) {
      newErrors.numberOfBreaks = "Number of Breaks is required";
    } else if (Number(formData.numberOfBreaks) < 0) {
      newErrors.numberOfBreaks = "Number of Breaks cannot be negative";
    } else if (Number(formData.numberOfBreaks) > 10) {
      newErrors.numberOfBreaks = "Number of Breaks cannot exceed 10";
    }

    // Break validations
    formData.breaks.forEach((b, i) => {
      if (!b.breakType.trim()) {
        newErrors[`breakType_${i}`] = `Break ${i + 1} type is required`;
      }

      if (!b.from) {
        newErrors[`breakFrom_${i}`] = `Break ${i + 1} start time is required`;
      }

      if (!b.to) {
        newErrors[`breakTo_${i}`] = `Break ${i + 1} end time is required`;
      }

      // Validate break timing (From Time must be before To Time)
      if (b.from && b.to && !isFromBeforeTo(b.from, b.to)) {
        newErrors[`breakTo_${i}`] =
          `Break ${i + 1} end time must be after start time`;
      }

      // Validate break is within shift timing
      if (b.from && b.to && !isWithinShift(b.from, b.to)) {
        const formattedFrom = formatTimeForDisplay(formData.fromTime);
        const formattedTo = formatTimeForDisplay(formData.toTime);
        newErrors[`breakTo_${i}`] =
          `Break ${i + 1} must be within shift hours (${formattedFrom} to ${formattedTo})`;
      }
    });

    // Period validations
    formData.periods.forEach((p, i) => {
      if (!p.periodName.trim()) {
        newErrors[`periodName_${i}`] = `Period ${i + 1} name is required`;
      }

      if (!p.from) {
        newErrors[`periodFrom_${i}`] = `Period ${i + 1} start time is required`;
      }

      if (!p.to) {
        newErrors[`periodTo_${i}`] = `Period ${i + 1} end time is required`;
      }

      // Validate period timing (From Time must be before To Time)
      if (p.from && p.to && !isFromBeforeTo(p.from, p.to)) {
        newErrors[`periodTo_${i}`] =
          `Period ${i + 1} end time must be after start time`;
      }

      // Validate period is within shift timing
      if (p.from && p.to && !isWithinShift(p.from, p.to)) {
        const formattedFrom = formatTimeForDisplay(formData.fromTime);
        const formattedTo = formatTimeForDisplay(formData.toTime);
        newErrors[`periodTo_${i}`] =
          `Period ${i + 1} must be within shift hours (${formattedFrom} to ${formattedTo})`;
      }
    });

    // Overlap validation: Check breaks vs periods
    formData.breaks.forEach((b, bIndex) => {
      if (b.from && b.to) {
        formData.periods.forEach((p, pIndex) => {
          if (p.from && p.to) {
            if (hasOverlap(b.from, b.to, p.from, p.to)) {
              if (!newErrors[`breakTo_${bIndex}`]) {
                newErrors[`breakTo_${bIndex}`] =
                  `Break ${bIndex + 1} overlaps with Period ${pIndex + 1}`;
              }
              if (!newErrors[`periodTo_${pIndex}`]) {
                newErrors[`periodTo_${pIndex}`] =
                  `Period ${pIndex + 1} overlaps with Break ${bIndex + 1}`;
              }
            }
          }
        });
      }
    });

    // Overlap validation: Check periods vs periods
    formData.periods.forEach((p1, i) => {
      if (p1.from && p1.to) {
        formData.periods.forEach((p2, j) => {
          if (i < j && p2.from && p2.to) {
            if (hasOverlap(p1.from, p1.to, p2.from, p2.to)) {
              if (!newErrors[`periodTo_${i}`]) {
                newErrors[`periodTo_${i}`] =
                  `Period ${i + 1} overlaps with Period ${j + 1}`;
              }
              if (!newErrors[`periodTo_${j}`]) {
                newErrors[`periodTo_${j}`] =
                  `Period ${j + 1} overlaps with Period ${i + 1}`;
              }
            }
          }
        });
      }
    });

    // Overlap validation: Check breaks vs breaks
    formData.breaks.forEach((b1, i) => {
      if (b1.from && b1.to) {
        formData.breaks.forEach((b2, j) => {
          if (i < j && b2.from && b2.to) {
            if (hasOverlap(b1.from, b1.to, b2.from, b2.to)) {
              if (!newErrors[`breakTo_${i}`]) {
                newErrors[`breakTo_${i}`] =
                  `Break ${i + 1} overlaps with Break ${j + 1}`;
              }
              if (!newErrors[`breakTo_${j}`]) {
                newErrors[`breakTo_${j}`] =
                  `Break ${j + 1} overlaps with Break ${i + 1}`;
              }
            }
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------- API PAYLOAD PREPARATION ---------------- */

  const prepareApiPayload = () => {
    // Format time to "HH:MM" format for API (24-hour format)
    const formatTimeForAPI = (timeValue) => {
      if (!timeValue) return "";

      try {
        // If it's a Day.js object
        if (dayjs.isDayjs(timeValue)) {
          return timeValue.format("HH:mm");
        }

        // If it's already in HH:MM format
        if (
          typeof timeValue === "string" &&
          timeValue.match(/^\d{1,2}:\d{2}$/)
        ) {
          // Ensure two-digit format
          const [hours, minutes] = timeValue.split(":");
          const formattedHours = hours.padStart(2, "0");
          const formattedMinutes = minutes.padStart(2, "0");
          return `${formattedHours}:${formattedMinutes}`;
        }

        // For any other string format
        if (typeof timeValue === "string") {
          const minutes = timeToMinutes(timeValue);
          if (minutes === null) return "";

          const hours = Math.floor(minutes / 60);
          const mins = minutes % 60;

          const formattedHours = hours.toString().padStart(2, "0");
          const formattedMinutes = mins.toString().padStart(2, "0");
          return `${formattedHours}:${formattedMinutes}`;
        }

        return "";
      } catch (error) {
        console.error("Error formatting time for API:", timeValue, error);
        return "";
      }
    };

    // Prepare periods_break_details array
    const periodsBreakDetails = [];

    // Add periods (is_period = 1)
    formData.periods.forEach((period) => {
      periodsBreakDetails.push({
        id: isEditMode && period.id ? period.id : "", // Use existing ID in edit mode, empty string in create mode
        is_period: 1,
        name: period.periodName,
        from_time: formatTimeForAPI(period.from),
        to_time: formatTimeForAPI(period.to),
      });
    });

    // Add breaks (is_period = 0)
    formData.breaks.forEach((breakItem) => {
      periodsBreakDetails.push({
        id: isEditMode && breakItem.id ? breakItem.id : "", // Use existing ID in edit mode, empty string in create mode
        is_period: 0,
        name: breakItem.breakType,
        from_time: formatTimeForAPI(breakItem.from),
        to_time: formatTimeForAPI(breakItem.to),
      });
    });

    // Get current timestamp
    const getCurrentTimestamp = () => {
      const now = new Date();
      return now.toISOString().slice(0, 19).replace("T", " ");
    };

    return {
      id: isEditMode ? shiftId : "", // Use shiftId in edit mode, empty string in create mode
      client_id: client_id, // from TokenService
      school_id: school_id, // from TokenService or navigation state
      shift_type: formData.shiftName,
      from_time: formatTimeForAPI(formData.fromTime),
      to_time: formatTimeForAPI(formData.toTime),
      number_of_periods: parseInt(formData.numberOfPeriods, 10) || 0,
      number_of_break: parseInt(formData.numberOfBreaks, 10) || 0,
      created_by: user_id,
      created_at: getCurrentTimestamp(),
      periods_break_details: periodsBreakDetails,
    };
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      console.log("Validation errors:", errors);
      return;
    }
    const payload = prepareApiPayload();
    setIsLoading(true);
    try {
      // Use the same endpoint for both create and update
      const response = await AxiosInstance.post("/school/shiftcreate", payload);
      setModalState({
        open: true,
        type: "success",
        message: response.data?.message,
        redirectPath: "/user/timetable",
      });
    } catch (error) {
      console.error("API Error:", error);

      setModalState({
        open: true,
        type: "error",
        message: error?.response?.data?.message || error?.message,
        redirectPath: "",
      });
    } finally {
      setIsLoading(false);
    }
  };
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;

    setModalState((prev) => ({
      ...prev,
      open: false,
    }));

    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <PopupModal
        open={deleteModalOpen}
        title="Confirm Delete"
        message={translations?.confirmDelete}
        onOk={handleDelete}
        onClose={() => setDeleteModalOpen(false)}
      />
      <form onSubmit={handleSubmit} className="container-fluid">
        {/* Loading indicator while fetching data */}
        {isFetching && (
          <div className="text-center my-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading shift data...</span>
            </div>
            <p className="mt-2">Loading shift data...</p>
          </div>
        )}

        {/* ================= SHIFT DETAILS ================= */}
        {!isFetching && (
          <>
            <div className={styles.card}>
              <div className={styles.container}>
                {/* Header */}
                <div className={styles.header}>
                  <div className={styles.headerIcon}>
                    <FaCalendarAlt />
                  </div>
                  <h1 className={styles.headerTitle}>
                    {isEditMode
                      ? translations?.labels?.editShift
                      : translations?.labels?.createShift}
                  </h1>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4 px-2">
                  <CustomTextInput
                    label={translations?.labels?.shiftName}
                    name="shiftName"
                    value={formData.shiftName}
                    onChange={handleInputChange}
                    error={errors.shiftName}
                    placeholder={translations?.labels?.shiftNamePlaceholder}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="col-md-4 px-2">
                  <CustomTimePicker
                    label={translations?.labels?.fromTime}
                    value={formData.fromTime}
                    onChange={(val) => handleTimeChange("fromTime", val)}
                    error={errors.fromTime}
                    placeholder={translations?.labels?.fromTimePlaceholder}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="col-md-4 px-2">
                  <CustomTimePicker
                    label={translations?.labels?.toTime}
                    value={formData.toTime}
                    onChange={(val) => handleTimeChange("toTime", val)}
                    error={errors.toTime}
                    placeholder={translations?.labels?.toTimePlaceholder}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 px-2">
                  <CustomTextInput
                    label={translations?.labels?.numberOfBreaks}
                    name="numberOfBreaks"
                    value={formData.numberOfBreaks}
                    onChange={handleInputChange}
                    error={errors.numberOfBreaks}
                    type="text"
                    placeholder={
                      translations?.labels?.numberOfBreaksPlaceholder
                    }
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="col-md-4 px-2">
                  <CustomTextInput
                    label={translations?.labels?.numberOfPeriods}
                    name="numberOfPeriods"
                    value={formData.numberOfPeriods}
                    disabled
                    error={errors.numberOfPeriods}
                    type="text"
                    placeholder={
                      translations?.labels?.numberOfPeriodsPlaceholder
                    }
                    required
                  />
                </div>
              </div>
            </div>

            {/* ================= BREAK DETAILS ================= */}
            <div className={styles.card}>
              <div className={styles.container}>
                {/* Header */}
                <div className={styles.header}>
                  <div className={styles.headerIcon}>
                    <FaCalendarAlt />
                  </div>
                  <h1 className={styles.headerTitle}>
                    {translations?.labels?.breakDetails}
                  </h1>
                </div>
              </div>

              {formData.breaks.map((b, index) => (
                <div className="row mb-3" key={index}>
                  <div className="col-md-4 px-2">
                    <CustomTextInput
                      label={`${translations?.labels?.breakType} ${index + 1}`}
                      value={b.breakType}
                      error={errors[`breakType_${index}`]}
                      onChange={(e) => {
                        const updated = [...formData.breaks];
                        updated[index].breakType = e.target.value;
                        setFormData((prev) => ({ ...prev, breaks: updated }));
                        // Clear error for this field
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[`breakType_${index}`];
                          return newErrors;
                        });
                      }}
                      placeholder={translations?.labels?.breakTypePlaceholder}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="col-md-4 px-2">
                    <CustomTimePicker
                      label={translations?.labels?.fromTime}
                      value={b.from}
                      error={errors[`breakFrom_${index}`]}
                      onChange={(val) => {
                        const updated = [...formData.breaks];
                        updated[index].from = val;
                        setFormData((prev) => ({ ...prev, breaks: updated }));
                        // Clear error for this field
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[`breakFrom_${index}`];
                          return newErrors;
                        });
                      }}
                      placeholder={translations?.labels?.fromTimePlaceholder}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="col-md-4 px-2">
                    <CustomTimePicker
                      label={translations?.labels?.toTime}
                      value={b.to}
                      error={errors[`breakTo_${index}`]}
                      onChange={(val) => {
                        const updated = [...formData.breaks];
                        updated[index].to = val;
                        setFormData((prev) => ({ ...prev, breaks: updated }));
                        // Clear error for this field
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[`breakTo_${index}`];
                          return newErrors;
                        });
                      }}
                      placeholder={translations?.labels?.toTimePlaceholder}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* ================= PERIOD DETAILS ================= */}
            <div className={styles.card}>
              <div className={styles.container}>
                {/* Header */}
                <div className={styles.header}>
                  <div className={styles.headerIcon}>
                    <FaCalendarAlt />
                  </div>
                  <h1 className={styles.headerTitle}>
                    {translations?.labels?.periodDetails}
                  </h1>
                </div>
              </div>

              {formData.periods.map((p, index) => (
                <div className="row mb-3" key={index}>
                  <div className="col-md-4 px-2">
                    <CustomTextInput
                      label={`${translations?.labels?.periodName} ${index + 1}`}
                      value={p.periodName}
                      error={errors[`periodName_${index}`]}
                      onChange={(e) => {
                        const updated = [...formData.periods];
                        updated[index].periodName = e.target.value;
                        setFormData((prev) => ({ ...prev, periods: updated }));
                        // Clear error for this field
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[`periodName_${index}`];
                          return newErrors;
                        });
                      }}
                      placeholder={translations?.labels?.periodNamePlaceholder}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="col-md-4 px-2">
                    <CustomTimePicker
                      label={translations?.labels?.fromTime}
                      value={p.from}
                      error={errors[`periodFrom_${index}`]}
                      onChange={(val) => {
                        const updated = [...formData.periods];
                        updated[index].from = val;
                        setFormData((prev) => ({ ...prev, periods: updated }));
                        // Clear error for this field
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[`periodFrom_${index}`];
                          return newErrors;
                        });
                      }}
                      placeholder={translations?.labels?.fromTimePlaceholder}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="col-md-4 px-2">
                    <CustomTimePicker
                      label={translations?.labels?.toTime}
                      value={p.to}
                      error={errors[`periodTo_${index}`]}
                      onChange={(val) => {
                        const updated = [...formData.periods];
                        updated[index].to = val;
                        setFormData((prev) => ({ ...prev, periods: updated }));
                        // Clear error for this field
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[`periodTo_${index}`];
                          return newErrors;
                        });
                      }}
                      placeholder={translations?.labels?.toTimePlaceholder}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* ================= ACTIONS ================= */}
            {/* ================= ACTIONS ================= */}
            <div className="row mt-4">
              <div className="col d-flex justify-content-center gap-3">
                {/* Delete Shift - only in edit mode */}
                {isEditMode && (
                  <CustomButton
                    type="button"
                    variant="primary"
                    size="sm"
                    fullWidth={false}
                    onClick={() => setDeleteModalOpen(true)}
                    disabled={isLoading}
                    style={{
                      backgroundColor: "#dc3545",
                      borderColor: "#dc3545",
                      color: "#fff",
                    }}
                  >
                    {translations?.labels?.deleteShift || "Delete Shift"}
                  </CustomButton>
                )}

                <CustomButton
                  type="button"
                  variant="primary"
                  size="sm"
                  fullWidth={false}
                  onClick={() => {
                    navigate(-1);
                  }}
                  disabled={isLoading}
                >
                  {translations?.labels?.cancelButton}
                </CustomButton>

                <CustomButton
                  type="submit"
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
                  style={{
                    backgroundColor: "#ff7a18",
                    borderColor: "#ff7a18",
                    color: "#fff",
                  }}
                  disabled={isLoading}
                >
                  {isLoading
                    ? translations?.labels?.saving
                    : isEditMode
                      ? translations?.labels?.updateButton
                      : translations?.labels?.saveButton}
                </CustomButton>
              </div>
            </div>
          </>
        )}
      </form>
    </>
  );
}

export default ShiftCreate;
